import React, { useState, useEffect } from 'react';
import {
  FileText,
  Search,
  CheckCircle,
  Clock,
  Calendar,
  MapPin,
  User,
  Mail,
  Phone,
  MessageCircle,
  Paperclip,
  Download,
  Building,
  Star,
  X,
  Copy,
  Eye,
  Loader,
  Send,
  RotateCcw,
} from 'lucide-react';
import i18n from '../i18n';

import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import ComplaintService from '../services/complaintService'; // Import your service
import { getMediaUrl } from '../config/apiConfig';
import ApiService from '../services/api';

import TranslatedText from '../components/TranslatedText';
// import { useLocation } from 'react-router-dom';

// Convert English digits to Devanagari script
function toDevanagariDigits(num) {
  const devanagariDigits = '०१२३४५६७८९';
  return num.toString().replace(/\d/g, (digit) => devanagariDigits[digit]);
}

function TrackComplaintPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { complaintIdGenerated } = location.state || {};
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;

  // Convert numbers based on language - returns Devanagari digits for Marathi, English digits otherwise
  const formatNumberForLanguage = (num, lang) => {
    if (!num && num !== 0) return '';
    if (lang === 'mr') {
      return toDevanagariDigits(num);
    }
    return num.toString();
  };

  const [searchType, setSearchType] = useState('id'); // 'id' or 'mobile'
  const [complaintId, setComplaintId] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [complaint, setComplaint] = useState(null);
  const [complaints, setComplaints] = useState([]); // For mobile search results
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [comments, setComments] = useState([]);
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [reopenReason, setReopenReason] = useState('');
  const [ratingLoading, setRatingLoading] = useState(false);
  const [ratingError, setRatingError] = useState('');
  const [ratingSuccess, setRatingSuccess] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const userRole = localStorage.getItem('userRole');
  const [showReopenForm, setShowReopenForm] = useState(false);
  const [isReopened, setIsReopened] = useState(false);
  const [reopenLoading, setReopenLoading] = useState(false);
  const [reopenError, setReopenError] = useState('');
  const [reopenSuccess, setReopenSuccess] = useState('');

  const handleReopenComplaint = async () => {
    if (!reopenReason || !reopenReason.trim()) {
      alert('Please enter a reason to reopen.');
      return;
    }
    if (!complaint?.id && !complaint?.complaintId) {
      alert('Invalid complaint.');
      return;
    }

    try {
      setReopenLoading(true);
      setReopenError('');
      setReopenSuccess('');

      const phoneNumber = complaint?.mobile || '';
      const complaintPrimaryId = complaint.id || complaint.complaintId;

      await ComplaintService.reopenComplaint(complaintPrimaryId, {
        reason: reopenReason.trim(),
        phoneNumber,
      });

      setIsReopened(true);
      setShowReopenForm(false);
      setReopenSuccess('Complaint reopened successfully.');
      // Optimistic UI update
      setComplaint((prev) => ({ ...prev, status: 'Reopen' }));
      setReopenReason('');
    } catch (err) {
      console.error('Failed to reopen complaint:', err);
      setReopenError(
        err?.message || 'Failed to reopen complaint. Please try again.'
      );
    } finally {
      setReopenLoading(false);
    }
  };
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  useEffect(() => {
    if (complaintIdGenerated) {
      setComplaintId(complaintIdGenerated);
    }
    // eslint-disable-next-line
  }, []);
  // Helper function to fetch rating data for resolved complaints
  const fetchRatingData = async (complaintData) => {
    if (complaintData.status?.toLowerCase() === 'resolved') {
      try {

        const ratingData = await ComplaintService.getRating(complaintData.id);

        if (ratingData && ratingData.rating) {
          setRating(ratingData.rating);
          setComment(ratingData.comment || '');
          setShowRating(false); // Don't show rating form if already rated

        } else {
          setShowRating(true); // Show rating form if not rated yet

        }
      } catch (ratingErr) {
        console.warn('Failed to fetch rating or no rating found:', ratingErr);
        // If no rating found, show rating form
        setShowRating(true);
      }
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return null;

    let date;
    // Handle Array format [yyyy, mm, dd, hh, mm, ss]
    if (Array.isArray(dateString)) {
      const [year, month, day, hour = 0, minute = 0, second = 0] = dateString;
      // Construct ISO string with IST offset to treat components as IST
      const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}+05:30`;
      date = new Date(iso);
    } else {
      // Handle String format
      let isoString = dateString.replace(' ', 'T');
      // If no timezone indicator, assume IST (+05:30) instead of UTC (Z)
      if (!isoString.endsWith('Z') && !isoString.includes('+')) {
        isoString += '+05:30';
      }
      date = new Date(isoString);
    }

    // Invalid date check
    if (isNaN(date.getTime())) {
      return formatNumberForLanguage(dateString, currentLanguage);
    }

    // Use Intl.DateTimeFormat to force Asia/Kolkata timezone
    const options = {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };

    try {
      const formatter = new Intl.DateTimeFormat('en-IN', options);
      const parts = formatter.formatToParts(date);
      const getPart = (type) => parts.find(p => p.type === type)?.value;

      const day = getPart('day');
      const month = getPart('month');
      const year = getPart('year');
      const hour = getPart('hour');
      const minute = getPart('minute');
      const dayPeriod = getPart('dayPeriod');

      const dateTimeStr = `${day}/${month}/${year} ${hour}:${minute} ${dayPeriod.toUpperCase()}`;
      return formatNumberForLanguage(dateTimeStr, 'en');
    } catch (e) {
      console.error('Date formatting error:', e);
      return date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'submitted':
        return 'bg-blue-100 text-blue-800';
      case 'in progress':
      case 'under review':
        return 'bg-yellow-100 text-yellow-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'reopen':
      case 'reopened':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Check for complaint data passed via navigation state
  useEffect(() => {
    if (location.state?.complaint) {
      const passedComplaint = location.state.complaint;
      setComplaint(passedComplaint);

      // Fetch additional data for the complaint
      const fetchComplaintDetails = async () => {
        try {
          // Fetch comments
          const commentData = await ComplaintService.getComments(
            passedComplaint.id
          );
          setComments(Array.isArray(commentData) ? commentData : []);

          // Fetch rating data for resolved complaints
          await fetchRatingData(passedComplaint);
        } catch (err) {
          console.warn('Failed to fetch additional complaint details:', err);
        }
      };

      fetchComplaintDetails();
    }
  }, [location.state]);

  // Handle ESC key to close image modal
  useEffect(() => {
    if (!showImageModal) return;

    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        setShowImageModal(false);
        setSelectedImage(null);
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [showImageModal]);

  const sampleComplaints = [{ id: 'CMP001' }, { id: 'CMP002' }];
  // helper function to download via fetch -> blob -> object URL
  const downloadFile = async (url, fileName, token) => {
    try {
      const response = await fetch(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined, // if your API is protected
        },
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error('Error downloading file:', err);
    }
  };

  const normalizeAttachmentEndpoint = (rawUrl) => {
    if (!rawUrl) return null;
    try {
      if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
        const parsed = new URL(rawUrl);
        const pathWithQuery = `${parsed.pathname}${parsed.search || ''}`;
        if (pathWithQuery.startsWith('/api')) {
          return pathWithQuery.replace('/api', '') || '/';
        }
        return pathWithQuery || '/';
      }
    } catch (_) { }
    let normalized = rawUrl.startsWith('/api') ? rawUrl.replace('/api', '') : rawUrl;
    if (!normalized.startsWith('/')) normalized = `/${normalized}`;
    return normalized;
  };

  // Handle viewing image in modal
  const handleViewImage = async (attachment, complaintId, isOfficer = false) => {
    try {
      const endpoint = normalizeAttachmentEndpoint(
        attachment.url || attachment.fileUrl || attachment.path
      );
      if (!endpoint) {
        alert('Unable to preview this attachment. Missing file path.');
        return;
      }

      const blob = await ApiService.getBlob(endpoint);
      const objectUrl = URL.createObjectURL(blob);

      const fileType = attachment.fileType || '';
      const ext = fileType.includes('/')
        ? fileType.split('/')[1]
        : 'bin';
      const filename = isOfficer
        ? `${complaintId}_resolution.${ext}`
        : `${complaintId}.${ext}`;

      // ✅ If it's a PDF, open it directly in a new tab
      if (fileType === 'application/pdf' || filename.toLowerCase().endsWith('.pdf')) {
        window.open(objectUrl, '_blank');
        return; // Stop here, don't show modal
      }

      // 🖼 For images and others — use modal
      setSelectedImage((prev) => {
        if (prev?.isObjectUrl && prev.url) {
          URL.revokeObjectURL(prev.url);
        }
        return {
          url: objectUrl,
          filename,
          attachment,
          downloadEndpoint: endpoint,
          isObjectUrl: true,
        };
      });
      setShowImageModal(true);
    } catch (error) {
      console.error('Error loading image:', error);
      alert('Error loading image. Please try again.');
    }
  };

  // Handle download from modal
  const handleDownloadFromModal = async () => {
    // 1. Get data from where the 'Eye' button stores it
    const data = selectedImage?.attachment || selectedImage;

    if (!data) {
      alert('No image data found.');
      return;
    }

    // 2. Locate the path. We check multiple names to ensure we don't get 'undefined'
    // In handleViewImage we set 'downloadEndpoint', so let's check that too.
    const rawPath = selectedImage.downloadEndpoint || data.url || data.fileUrl || data.path || data.filePath;

    if (!rawPath) {
      alert('Missing file path. Cannot download.');
      return;
    }

    // 3. Construct the clean URL (This removes 'apiundefined')
    const endpoint = normalizeAttachmentEndpoint(rawPath);

    try {
      const blob = await ApiService.getBlob(endpoint);
      const objectUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = objectUrl;

      // Determine filename
      // If selectedImage.filename is set, use it. Otherwise construct one.
      const filename = selectedImage.filename || `download.${data.fileType?.split('/')[1] || 'bin'}`;
      link.download = filename;

      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Download failed:', error);
      // If the Blob/API method fails, try a direct browser download as a last resort
      window.open(endpoint, '_blank');
    }
  };

  // Close image modal and cleanup
  const handleCloseImageModal = () => {
    // No need to revoke URL since we're using direct URLs, not blob URLs
    setShowImageModal(false);
    setSelectedImage(null);
  };

  const handleSearch = async () => {
    const searchValue =
      searchType === 'id' ? complaintId.trim() : mobileNumber.trim();

    if (!searchValue) {
      setError(
        searchType === 'id'
          ? t('track.form.pleaseEnter') || 'Please enter a complaint ID'
          : 'Please enter a mobile number'
      );
      return;
    }

    // Validate mobile number format if searching by mobile
    if (searchType === 'mobile') {
      const mobileRegex = /^[+]?[91]?[0-9]{10}$/;
      if (!mobileRegex.test(searchValue.replace(/[\s\-()]/g, ''))) {
        setError('Please enter a valid 10-digit mobile number');
        return;
      }
    }

    // Validate Complaint ID format
    if (searchType === 'id') {
      // Check if input looks exactly like a 10-digit mobile number
      const potentialMobileRegex = /^[0-9]{10}$/;
      if (potentialMobileRegex.test(searchValue)) {
        setError(t('track.error.mobileInIdField'));
        return;
      }

      // "Professional" validation: Check for minimum length or invalid characters
      // Assuming IDs should be at least 3 characters
      if (searchValue.length < 3) {
        setError(t('track.error.invalidId'));
        return;
      }
    }

    setLoading(true);
    setError('');
    setComplaint(null);
    setComplaints([]);
    // Reset rating state
    setShowRating(false);
    setRating(0);
    setComment('');
    setRatingError('');
    setRatingSuccess('');

    try {
      if (searchType === 'id') {
        const langCode = i18n.language;
        const data = await ComplaintService.trackComplaint(
          searchValue,
          langCode
        );
        setComplaint(data);



        // Fetch comments and check if already rated
        try {
          const commentData = await ComplaintService.getComments(data.id);
          setComments(Array.isArray(commentData) ? commentData : []);
        } catch (err) {
          console.warn('Failed to fetch comments:', err);
          setComments([]);
        }

        // Fetch rating data for resolved complaints
        await fetchRatingData(data);
      } else {
        const data = await ComplaintService.trackComplaint(searchValue);
        if (Array.isArray(data) && data.length > 0) {
          if (data.length === 1) {
            setComplaint(data[0]);
            // Fetch rating data for single complaint
            await fetchRatingData(data[0]);
          } else {
            setComplaints(data);
          }
        } else {
          throw new Error('No complaints found');
        }
      }
    } catch (err) {
      setError(
        searchType === 'id'
          ? t('track.notFound') ||
          'Complaint not found. Please check your ID and try again.'
          : 'No complaints found for this mobile number. Please check and try again.'
      );
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen px-4 py-4 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 sm:py-8">
      <div className="w-full max-w-4xl mx-auto">
        {/* Page Header */}
        <div className="mb-6 text-center sm:mb-8">
          <h1 className="mb-2 text-2xl font-bold text-gray-900 sm:text-3xl">
            {t('track.title')}
          </h1>
          <p
            className="mb-2 text-base font-medium sm:text-lg"
            style={{ color: '#B8860B' }}
          >
            {t('track.titlenote')}
          </p>
          <p className="max-w-2xl px-2 mx-auto text-base text-gray-600 sm:text-lg">
            {t('track.subtitle')}
          </p>
        </div>

        {/* Search Form - Centered */}
        <div className="max-w-2xl mx-auto mb-6 sm:mb-8">
          <div
            className="p-4 border shadow-2xl rounded-2xl border-blue-200/30 bg-white/90 backdrop-blur-md sm:rounded-3xl sm:p-6 lg:p-8"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 20%, rgba(184, 134, 11, 0.08) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(218, 165, 32, 0.06) 0%, transparent 50%)',
            }}
          >
            <h2 className="flex items-center justify-center gap-2 mb-4 text-base font-bold text-center text-gray-900 break-words sm:mb-6 sm:text-lg">
              <Search
                className="flex-shrink-0 w-4 h-4 sm:h-5 sm:w-5"
                style={{ color: '#B8860B' }}
              />
              <span>{t('track.title')}</span>
            </h2>

            <div className="space-y-4 sm:space-y-5">
              {/* Search Type Selector - Mobile optimized */}
              <div>
                <label className="block mb-3 text-sm font-medium text-gray-700">
                  {t('track.form.searchmethod')}
                </label>
                <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
                  <label className="flex items-center p-3 transition-colors border border-gray-200 rounded-lg hover:bg-gray-50 sm:rounded-none sm:border-0 sm:p-2 sm:hover:bg-transparent">
                    <input
                      type="radio"
                      value="id"
                      checked={searchType === 'id'}
                      onChange={(e) => setSearchType(e.target.value)}
                      className="flex-shrink-0 w-4 h-4 text-yellow-600 border-gray-300 focus:ring-yellow-500"
                      style={{ accentColor: '#B8860B' }}
                    />
                    <span className="ml-3 text-sm font-medium text-gray-700 sm:ml-2">
                      {t('track.form.id')}
                    </span>
                  </label>
                  <label className="flex items-center p-3 transition-colors border border-gray-200 rounded-lg hover:bg-gray-50 sm:rounded-none sm:border-0 sm:p-2 sm:hover:bg-transparent">
                    <input
                      type="radio"
                      value="mobile"
                      checked={searchType === 'mobile'}
                      onChange={(e) => setSearchType(e.target.value)}
                      className="flex-shrink-0 w-4 h-4 text-yellow-600 border-gray-300 focus:ring-yellow-500"
                      style={{ accentColor: '#B8860B' }}
                    />
                    <span className="ml-3 text-sm font-medium text-gray-700 sm:ml-2">
                      {t('track.form.mobilenumber')}
                    </span>
                  </label>
                </div>
              </div>

              {/* Search Input - Mobile optimized */}
              {searchType === 'id' ? (
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    {t('track.form.id') || 'Complaint ID'}
                  </label>
                  <input
                    type="text"
                    value={complaintId || complaintIdGenerated}
                    onChange={(e) => setComplaintId(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder={t('track.form.placeholder')}
                    className="w-full px-3 py-3 text-base transition-colors border border-gray-300 rounded-lg focus:border-transparent focus:ring-2 focus:ring-yellow-500 sm:px-4 sm:py-3"
                  />
                </div>
              ) : (
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    {t('track.form.mobilenumber')}
                  </label>
                  <input
                    type="tel"
                    value={mobileNumber}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/[^0-9]/g, '')
                        .slice(0, 10);
                      setMobileNumber(value);
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder={t('track.form.placeholdermobile')}
                    className="w-full px-3 py-3 text-base transition-colors border border-gray-300 rounded-lg focus:border-transparent focus:ring-2 focus:ring-yellow-500 sm:px-4 sm:py-3"
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                </div>
              )}

              {error && (
                <div className="p-3 border border-red-200 rounded-lg bg-red-50">
                  <p className="text-sm leading-relaxed text-red-600 break-words">
                    {error}
                  </p>
                </div>
              )}

              <button
                onClick={handleSearch}
                disabled={loading}
                className="w-full text-white py-3 sm:py-4 px-4 sm:px-6 rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.01] sm:hover:scale-[1.02] transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{
                  background:
                    'linear-gradient(135deg, #B8860B 0%, #DAA520 50%, #B8860B 100%)',
                }}
              >
                {loading ? (
                  <>
                    <div className="flex-shrink-0 w-4 h-4 border-2 border-white rounded-full animate-spin border-t-transparent sm:h-5 sm:w-5"></div>
                    <span className="text-sm sm:text-base">
                      {t('track.form.searching')}
                    </span>
                  </>
                ) : (
                  <>
                    <Search className="flex-shrink-0 w-4 h-4 sm:h-5 sm:w-5" />
                    <span className="text-sm break-words sm:text-base">
                      {searchType === 'id'
                        ? t('track.form.trackbyid')
                        : t('track.form.trackbymobile')}
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Multiple Complaints List (for mobile search) */}
        {complaints.length > 0 && (
          <div className="max-w-4xl mx-auto mb-6 sm:mb-8">
            <div
              className="p-4 border shadow-2xl rounded-2xl border-blue-200/30 bg-white/90 backdrop-blur-md sm:rounded-3xl sm:p-6 lg:p-8"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 20% 20%, rgba(184, 134, 11, 0.08) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(218, 165, 32, 0.06) 0%, transparent 50%)',
              }}
            >
              <h3 className="flex flex-col gap-3 mb-4 text-lg font-bold text-gray-900 sm:flex-row sm:items-center sm:justify-between sm:text-xl">
                <div className="flex items-center gap-2">
                  <FileText
                    className="w-5 h-5 sm:h-6 sm:w-6"
                    style={{ color: '#B8860B' }}
                  />
                  <span className="text-base sm:text-xl">
                    {t('track.Found')}
                    {complaints.length} {t('track.ComplaintsforMobile')}{' '}
                    {mobileNumber}
                  </span>
                </div>
                {complaints.length > 10 && (
                  <span className="self-start px-3 py-1 text-xs text-gray-600 bg-yellow-100 rounded-full sm:self-auto sm:text-sm">
                    {t('track.Scrolltoseeall')}
                  </span>
                )}
              </h3>

              {/* Mobile-optimized complaint list */}
              <div
                className={`space-y-3 sm:space-y-4 ${complaints.length > 10 ? 'max-h-80 sm:max-h-96 overflow-y-auto pr-1 sm:pr-2' : ''}`}
              >
                {complaints.map((comp) => (
                  <div
                    key={comp.complaintId}
                    className="bg-white/70 backdrop-blur-sm border border-blue-200/50 rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-6 hover:shadow-lg transition-all duration-300 cursor-pointer hover:scale-[1.01] sm:hover:scale-[1.02]"
                    onClick={async () => {
                      setComplaint(comp);
                      await fetchRatingData(comp);
                    }}
                  >
                    {/* Mobile-first layout */}
                    <div className="space-y-3">
                      {/* ID and Status row - mobile optimized */}
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-base font-semibold text-gray-900 break-words sm:text-lg">
                            {t('track.id')}: {comp.complaintId}
                          </h4>
                        </div>
                        <span
                          className={`self-start px-3 py-1 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap ${getStatusColor(comp.status)}`}
                        >
                          <TranslatedText>{comp.status}</TranslatedText>
                        </span>
                      </div>

                      {/* Description - mobile optimized */}
                      <div className="min-w-0">
                        <p className="overflow-hidden text-sm leading-relaxed text-gray-600 break-all sm:text-base">
                          <TranslatedText>   {comp.description && comp.description.length > 120
                            ? `${comp.description.substring(0, 120)}...`
                            : comp.description || 'No description available'}</TranslatedText>
                        </p>
                      </div>

                      {/* View Details button - mobile optimized */}
                      <div className="flex justify-end pt-2 border-t border-gray-200">
                        <button
                          className="flex items-center gap-1 px-3 py-2 text-xs font-medium transition-colors rounded-md hover:bg-yellow-50 sm:rounded-lg sm:px-4 sm:text-sm"
                          style={{ color: '#B8860B' }}
                        >
                          <span>{t('track.ViewDetails')}</span>
                          <svg
                            className="w-3 h-3 sm:h-4 sm:w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {complaint && (
        <div className="fixed inset-0 z-50 flex items-start justify-center w-full h-full p-2 overflow-y-auto bg-black/40 backdrop-blur-sm sm:p-4">
          <div
            className="w-full max-w-4xl lg:max-w-5xl bg-white/95 backdrop-blur-md rounded-xl sm:rounded-2xl lg:rounded-3xl shadow-2xl border border-blue-200/30 max-h-[98vh] sm:max-h-[95vh] lg:max-h-[90vh] overflow-y-auto my-2 sm:my-4"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 20%, rgba(184, 134, 11, 0.08) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(218, 165, 32, 0.06) 0%, transparent 50%)',
            }}
          >
            <div className="p-3 sm:p-4 md:p-6 lg:p-8">
              {/* Modal Header with Close Button */}
              <div className="flex items-start justify-between pb-4 mb-6 border-b border-gray-200 sm:mb-8 sm:pb-6">
                {/* Left Side - Title and ID */}
                <div className="flex items-start flex-1 min-w-0 gap-3 sm:gap-4">
                  <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-full shadow-lg bg-gradient-to-r from-green-500 to-green-600 sm:h-12 sm:w-12">
                    <CheckCircle className="w-5 h-5 text-white sm:h-6 sm:w-6" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h2 className="mb-2 text-lg font-bold text-gray-900 sm:text-xl lg:text-2xl">
                      {t('track.details.title')}
                    </h2>

                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                      <span className="text-sm text-gray-600 sm:text-base">
                        {t('TRACK.ID')}
                      </span>

                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className="text-sm font-bold break-all sm:text-base lg:text-lg"
                          style={{ color: '#B8860B' }}
                        >
                          {complaint.complaintId}
                        </span>

                        {/* ✅ Safe Clipboard Copy with Fallback */}
                        <button
                          onClick={async () => {
                            try {
                              const textToCopy =
                                complaint?.complaintId?.toString() || '';

                              if (
                                typeof navigator !== 'undefined' &&
                                navigator.clipboard &&
                                typeof navigator.clipboard.writeText ===
                                'function'
                              ) {
                                await navigator.clipboard.writeText(textToCopy);
                              } else {
                                // ✅ Fallback for older or insecure contexts
                                const textArea =
                                  document.createElement('textarea');
                                textArea.value = textToCopy;
                                textArea.style.position = 'fixed';
                                textArea.style.left = '-9999px';
                                document.body.appendChild(textArea);
                                textArea.focus();
                                textArea.select();
                                document.execCommand('copy');
                                document.body.removeChild(textArea);
                              }

                              setCopySuccess(true);
                              setTimeout(() => setCopySuccess(false), 2000);
                            } catch (err) {
                              console.error('Failed to copy:', err);
                            }
                          }}
                          className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors group flex-shrink-0"
                          title="Copy Complaint ID"
                        >
                          <Copy className="w-3 h-3 text-gray-600 group-hover:text-yellow-600 sm:h-4 sm:w-4" />
                        </button>

                        {copySuccess && (
                          <span className="ml-1 text-xs font-medium text-green-600 animate-fade-in sm:text-sm">
                            {t('track.Copied!')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Close Button - Properly Aligned */}
                <button
                  onClick={() => setComplaint(null)}
                  className="flex items-center justify-center flex-shrink-0 w-8 h-8 ml-3 transition-colors bg-gray-100 rounded-full shadow-sm hover:bg-gray-200 sm:h-10 sm:w-10"
                  title="Close"
                >
                  <X className="w-4 h-4 text-gray-600 sm:h-5 sm:w-5" />
                </button>
              </div>

              {/* Status and Rating Section */}
              <div className="flex flex-col gap-4 mb-6 sm:mb-8 sm:flex-row sm:items-center sm:justify-between">

                {/* Status Badge + Reopen Button */}
                <div className="flex items-center gap-4 flex-1">
                  <span
                    className={`inline-block px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold shadow-md ${getStatusColor(complaint.status)}`}
                  >
               <TranslatedText>{complaint.status}</TranslatedText>
                  </span>

                  {/* Reopen Button */}
                  {complaint.status?.toLowerCase() === 'resolved' && !isReopened && (
                    <>
                      {!showReopenForm ? (
                        <button
                          onClick={() => setShowReopenForm(true)}
                          className="px-4 py-2 font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                        >
                          <TranslatedText>Reopen Complaint</TranslatedText>
                        </button>
                      ) : (
                        <div className="flex items-center gap-2 w-full">

                          <input
                            type="text"
                            placeholder={t('Reopen Reason')}
                            value={reopenReason}
                            onChange={(e) => setReopenReason(e.target.value)}
                            className="flex-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                          <button
                            onClick={handleReopenComplaint}
                            className="px-4 py-2 font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                          >
                            <TranslatedText>  Submit</TranslatedText>
                          </button>
                          <button
                            onClick={() => setShowReopenForm(false)}
                            className="px-4 py-2 font-medium text-white bg-gray-500 rounded-lg hover:bg-gray-600"
                          >
                            <TranslatedText>  Cancel </TranslatedText>
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>




                {/* Rating Section for Resolved Complaints */}
                {complaint.status?.toLowerCase() === 'resolved' && (
                  <div className="self-start p-3 bg-white border border-gray-200 rounded-lg shadow-sm sm:self-center">
                    {rating > 0 ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 sm:w-5 sm:h-5 ${star <= rating ? 'text-yellow-500' : 'text-gray-300'}`}
                              fill={star <= rating ? '#FBBF24' : 'none'}
                            />
                          ))}
                        </div>
                        <span className="text-xs font-medium text-center text-gray-600">
                          {t('track.rating')}
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 sm:w-5 sm:h-5 cursor-pointer ${star <= rating ? 'text-yellow-500' : 'text-gray-300'} hover:text-yellow-400 transition-colors`}
                              onClick={() => {
                                setRating(star);
                                setShowRating(true);
                              }}
                              fill={star <= rating ? '#FBBF24' : 'none'}
                            />
                          ))}
                        </div>
                        <span className="text-xs font-medium text-center text-gray-600">
                          {t('track.RateResolution')}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Rating Form - Show below header when rating */}
              {showRating && complaint.status?.toLowerCase() === 'resolved' && (
                <div className="p-4 mb-6 border border-yellow-200 rounded-xl bg-yellow-50">
                  <h4 className="mb-3 text-lg font-semibold text-gray-900">
                    {t('track.ratenow')}
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">
                        {t('track.rate')}
                      </span>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-6 h-6 cursor-pointer ${star <= rating ? 'text-yellow-500' : 'text-gray-300'} hover:text-yellow-400`}
                            onClick={() => setRating(star)}
                            fill={star <= rating ? '#FBBF24' : 'none'}
                          />
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        {t('track.Addyourfeedback ')}
                      </label>
                      <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-transparent focus:ring-2 focus:ring-yellow-500"
                        placeholder={t('track.Writeyourcommenthere...')}
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={async () => {
                          if (rating === 0) {
                            setRatingError(
                              'Please select a rating before submitting.'
                            );
                            return;
                          }

                          setRatingLoading(true);
                          setRatingError('');
                          setRatingSuccess('');
                          try {
                            await ComplaintService.submitRating({
                              complaintId: complaint.id,
                              rating,
                              comment,
                            });
                            setRatingSuccess('Thank you for your feedback!');
                            setShowRating(false);
                          } catch (err) {
                            setRatingError(
                              'Failed to submit rating. Please try again.'
                            );
                          }
                          setRatingLoading(false);
                        }}
                        disabled={ratingLoading}
                        className="px-6 py-2 font-medium text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {ratingLoading ? 'Submitting...' : t('track.Submit')}
                      </button>
                      <button
                        onClick={() => {
                          setShowRating(false);
                          setRating(0);
                          setComment('');
                          setRatingError('');
                        }}
                        className="px-6 py-2 font-medium text-white bg-gray-500 rounded-lg hover:bg-gray-600"
                      >
                        {t('track.Cancel')}
                      </button>
                    </div>
                    {ratingError && (
                      <div className="text-sm text-red-600">{ratingError}</div>
                    )}
                    {ratingSuccess && (
                      <div className="text-sm text-green-600">
                        {ratingSuccess}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Complaint Information Grid */}
              <div className="grid grid-cols-1 gap-4 mb-6 sm:mb-8 sm:gap-6 lg:grid-cols-2 lg:gap-8">
                {/* Basic Info */}
                <div className="p-4 border shadow-lg rounded-xl border-blue-200/30 bg-white/80 backdrop-blur-sm sm:rounded-2xl sm:p-6">
                  <div className="flex items-center gap-3 mb-4 sm:mb-6">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 sm:h-10 sm:w-10">
                      <FileText className="w-4 h-4 text-white sm:h-5 sm:w-5" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 sm:text-xl">
                      {t('track.BasicInformation')}
                    </h3>
                  </div>

                  <div className="space-y-4">
                    {/* Description */}
                    <div>
                      <label className="block mb-1 text-sm font-semibold text-gray-700">
                        {t('track.Description')}
                      </label>

                      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                        {/* Scrollable container added here */}
                        <div className="space-y-4 overflow-y-auto w-600 lg:w-4/4 max-h-64 md:w-full">
                          <TranslatedText
                            tag="p"
                            className="mt-1 text-sm text-gray-700 break-all"
                          >
                            {complaint.descriptionMarathi || complaint.description}
                          </TranslatedText>
                        </div>
                      </div>
                    </div>

                    {/* Village / Taluka Info */}
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                      <div>
                        <label className="block mb-1 text-sm font-semibold text-gray-700">
                          {t('track.Village')}
                        </label>
                        <p className="px-3 py-2 font-medium text-gray-900 break-words border rounded-lg bg-gray-50">
                          {i18n.language === 'mr'
                            ? complaint.villageMarathi || complaint.village
                            : complaint.village}
                        </p>
                      </div>

                      <div>
                        <label className="block mb-1 text-sm font-semibold text-gray-700">
                          {t('track.Taluka')}
                        </label>
                        <p className="px-3 py-2 font-medium text-gray-900 break-words border rounded-lg bg-gray-50">
                          {i18n.language === 'mr'
                            ? complaint.talukaMarathi || complaint.taluka
                            : complaint.taluka}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Applicant Details */}
                <div className="p-4 border shadow-lg rounded-xl border-blue-200/30 bg-white/80 backdrop-blur-sm sm:rounded-2xl sm:p-6">
                  <div className="flex items-center gap-3 mb-4 sm:mb-6">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-r from-teal-500 to-teal-600 sm:h-10 sm:w-10">
                      <User className="w-4 h-4 text-white sm:h-5 sm:w-5" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 sm:text-xl">
                      {t('track.Applicant Details')}
                    </h3>
                  </div>

                  <div className="space-y-3 sm:space-y-4">
                    {/* Name */}
                    <div className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
                      <User className="flex-shrink-0 w-4 h-4 text-gray-500 sm:h-5 sm:w-5" />
                      <span className="font-medium text-gray-900 break-words">
                        <TranslatedText>{complaint.name}</TranslatedText>
                      </span>
                    </div>

                    {/* Email */}
                    <div className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
                      <Mail className="flex-shrink-0 w-4 h-4 text-gray-500 sm:h-5 sm:w-5" />
                      <span className="font-medium text-gray-900 break-all">
                        {complaint.email}
                      </span>
                    </div>

                    {/* Mobile */}
                    <div className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
                      <Phone className="flex-shrink-0 w-4 h-4 text-gray-500 sm:h-5 sm:w-5" />
                      <span className="font-medium text-gray-900">
                        {formatNumberForLanguage(complaint.mobile, currentLanguage)}
                      </span>
                    </div>

                    {/* Address (with vertical scroll for long text) */}
                    <div className="flex items-start gap-3 p-3 border rounded-lg scrollbar-thin scrollbar-thumb-gray-300 bg-gray-50">
                      <MapPin className="flex-shrink-0 w-4 h-4 mt-1 text-gray-500 sm:h-5 sm:w-5" />
                      <span className="overflow-y-auto font-medium leading-relaxed text-gray-900 break-words max-h-28">
                        <TranslatedText>{complaint.address}</TranslatedText>
                      </span>
                    </div>
                  </div>
                </div>
              </div>


              {/* Citizen Attachments Section */}
              <div className="p-4 mb-6 border shadow-lg rounded-xl border-blue-200/30 bg-white/80 backdrop-blur-sm sm:mb-8 sm:rounded-2xl sm:p-6">
                <div className="flex items-center gap-3 mb-4 sm:mb-6">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 sm:h-10 sm:w-10">
                    <Paperclip className="w-4 h-4 text-white sm:h-5 sm:w-5" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 sm:text-xl">
                    {t('track.Citizen Attachments')}
                  </h3>
                </div>

                {complaint.attachments_citizen &&
                  complaint.attachments_citizen.length > 0 ? (
                  <div className="space-y-3">
                    {complaint.attachments_citizen.map((attachment, index) => {
                      const fullUrl = getMediaUrl(attachment.url);
                      const isImage =
                        attachment.fileType &&
                        attachment.fileType.startsWith('image/');
                      const ext = attachment.fileType?.split('/')[1] || 'bin';
                      const filename = `Citizen-${complaint.complaintId}-${index + 1}.${ext}`;

                      return (
                        <div
                          key={attachment.id || index}
                          className="p-4 transition-colors border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center flex-1 min-w-0 gap-3">
                              <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg">
                                {isImage ? (
                                  <svg
                                    className="w-5 h-5 text-blue-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
                                    />
                                  </svg>
                                ) : (
                                  <FileText className="w-5 h-5 text-blue-600" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {filename || `Attachment ${index + 1}`}
                                </p>

                                <p className="text-xs text-gray-500">
                                  Submitted by citizen •{' '}
                                  {attachment.size
                                    ? `${Math.round(attachment.size / 1024)}KB`
                                    : ''}{' '}
                                  • {attachment.fileType || 'Unknown type'}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2 ml-4">
                              <button
                                onClick={() =>
                                  handleViewImage(
                                    attachment,
                                    complaint.complaintId,
                                    false
                                  )
                                }
                                className="p-2 text-blue-600 transition-colors rounded-lg hover:bg-blue-50"
                                title="View"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={async () => {
                                  try {
                                    const normalizedEndpoint =
                                      attachment.url &&
                                        attachment.url.startsWith('/api')
                                        ? attachment.url.replace('/api', '')
                                        : attachment.url;
                                    const blob =
                                      await ApiService.getBlob(
                                        normalizedEndpoint
                                      );
                                    const objectUrl = URL.createObjectURL(blob);
                                    const ext = attachment.fileType
                                      ? attachment.fileType.split('/')[1] ||
                                      'bin'
                                      : 'bin';
                                    const filename = `${complaint.complaintId}.${ext}`;
                                    const link = document.createElement('a');
                                    link.href = objectUrl;
                                    link.download = filename;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    URL.revokeObjectURL(objectUrl);
                                  } catch (error) {
                                    console.error(
                                      'Error downloading file:',
                                      error
                                    );
                                    alert(
                                      'Error downloading file. Please try again.'
                                    );
                                  }
                                }}
                                className="p-2 text-blue-600 transition-colors rounded-lg hover:bg-blue-50"
                                title="Download"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="py-12 text-center border border-gray-200 rounded-xl bg-gray-50">
                    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-full">
                      <Paperclip className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-lg font-semibold text-gray-600">
                      {t('track.Nocitizenattachments')}
                    </p>
                    <p className="mt-2 text-sm text-gray-500">
                      {t('track.note1')}
                    </p>
                  </div>
                )}
              </div>

              {/* Officer Attachments Section */}
              <div className="p-6 mb-8 border shadow-lg rounded-2xl border-blue-200/30 bg-white/80 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-r from-green-500 to-green-600">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {t('track.Officer Attachments')}
                  </h3>
                </div>

                {complaint.attachments_officer &&
                  complaint.attachments_officer.length > 0 ? (
                  <div className="space-y-3">
                    {complaint.attachments_officer.map((attachment, index) => {
                      const fullUrl = getMediaUrl(attachment.url);
                      const isImage =
                        attachment.fileType &&
                        attachment.fileType.startsWith('image/');
                      const ext = attachment.fileType?.split('/')[1] || 'bin';
                      const filename = `Officer-${complaint.complaintId}-${index + 1}.${ext}`;

                      return (
                        <div
                          key={attachment.id || index}
                          className="p-4 transition-colors border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center flex-1 min-w-0 gap-3">
                              <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg">
                                {isImage ? (
                                  <svg
                                    className="w-5 h-5 text-green-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
                                    />
                                  </svg>
                                ) : (
                                  <FileText className="w-5 h-5 text-green-600" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {filename ||
                                    `Officer Attachment ${index + 1}`}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Submitted by officer •{' '}
                                  {attachment.size
                                    ? `${Math.round(attachment.size / 1024)}KB`
                                    : ''}{' '}
                                  • {attachment.fileType || 'Unknown type'}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2 ml-4">
                              <button
                                onClick={() =>
                                  handleViewImage(
                                    attachment,
                                    complaint.complaintId,
                                    true
                                  )
                                }
                                className="p-2 text-green-600 transition-colors rounded-lg hover:bg-green-50"
                                title="View"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={async () => {
                                  try {
                                    const normalizedEndpoint =
                                      attachment.url &&
                                        attachment.url.startsWith('/api')
                                        ? attachment.url.replace('/api', '')
                                        : attachment.url;
                                    const blob =
                                      await ApiService.getBlob(
                                        normalizedEndpoint
                                      );
                                    const objectUrl = URL.createObjectURL(blob);
                                    const ext = attachment.fileType
                                      ? attachment.fileType.split('/')[1] ||
                                      'bin'
                                      : 'bin';
                                    const filename = `${complaint.complaintId}_resolution.${ext}`;
                                    const link = document.createElement('a');
                                    link.href = objectUrl;
                                    link.download = filename;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    URL.revokeObjectURL(objectUrl);
                                  } catch (error) {
                                    console.error(
                                      'Error downloading file:',
                                      error
                                    );
                                    alert(
                                      'Error downloading file. Please try again.'
                                    );
                                  }
                                }}
                                className="p-2 text-green-600 transition-colors rounded-lg hover:bg-green-50"
                                title="Download"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="py-12 text-center border border-gray-200 rounded-xl bg-gray-50">
                    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-full">
                      <FileText className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-lg font-semibold text-gray-600">
                      {t('trackNoofficerattachments')}
                    </p>
                    <p className="mt-2 text-sm text-gray-500">
                      {t('track.note2')}
                    </p>
                  </div>
                )}
              </div>

              {/* Department & Assignment Info */}
              <div className="p-4 mb-6 border shadow-lg rounded-xl border-blue-200/30 bg-white/80 backdrop-blur-sm sm:mb-8 sm:rounded-2xl sm:p-6">
                <div className="flex items-center gap-3 mb-4 sm:mb-6">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-r from-purple-500 to-purple-600 sm:h-10 sm:w-10">
                    <Building className="w-4 h-4 text-white sm:h-5 sm:w-5" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 sm:text-xl">
                    {t('track.Department & Assignment')}
                  </h3>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
                  {/* Department */}
                  <div>
                    <label className="block mb-2 text-sm font-semibold text-gray-700">
                      {t('track.Department')}
                    </label>
                    <div className="px-4 py-3 border border-gray-200 rounded-lg bg-gray-50">
                      <p className="font-medium text-gray-900">
                        {i18n.language === 'mr'
                          ? complaint.departmentNameMarathi ||
                          complaint.departmentName
                          : complaint.departmentName}
                      </p>
                    </div>
                  </div>

                  {/* Assigned To
                  <div>
                    <label className="block mb-2 text-sm font-semibold text-gray-700">
                      {t('track.Assigned To')}
                    </label>
                    <div className="px-4 py-3 border border-gray-200 rounded-lg bg-gray-50">
                      <p className="font-medium text-gray-900">
                        {i18n.language === 'mr'
                          ? complaint.assignedToMarathi || complaint.assignedTo || t('NotAssignedYet')
                          : complaint.assignedTo || t('NotAssignedYet')}
                      </p>
                    </div>
                  </div> */}

                  {/* Submitted Date */}
                  <div>
                    <label className="block mb-2 text-sm font-semibold text-gray-700">
                      {t('track.Submitted Date')}
                    </label>
                    <div className="px-4 py-3 border border-gray-200 rounded-lg bg-gray-50">
                      <p className="font-medium text-gray-900">
                        {complaint.submittedDate ? formatDateTime(complaint.submittedDate) : '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Timeline */}
              {complaint.timeline && complaint.timeline.length > 0 && (
                <div className="p-6 mb-8 border shadow-lg rounded-2xl border-blue-200/30 bg-white/80 backdrop-blur-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-r from-pink-500 to-pink-600">
                      <Clock className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {t('track.Status Timeline')}
                    </h3>
                  </div>

                  <div
                    className={`space-y-6 ${complaint.timeline.length > 5
                      ? 'max-h-96 overflow-y-auto pr-2'
                      : ''
                      }`}
                  >
                    {[...complaint.timeline].reverse().map((event, index) => (
                      <div key={index} className="flex items-start">
                        <div className="flex flex-col items-center">
                          <div
                            className={`w-8 h-8 rounded-full shadow-lg flex items-center justify-center ${event.status === complaint.status
                              ? 'bg-gradient-to-r from-blue-500 to-blue-600'
                              : 'bg-gradient-to-r from-green-500 to-green-600'
                              }`}
                          >
                            {event.status === complaint.status ? (
                              <Clock className="w-4 h-4 text-white" />
                            ) : (
                              <CheckCircle className="w-4 h-4 text-white" />
                            )}
                          </div>
                          {index < complaint.timeline.length - 1 && (
                            <div className="w-0.5 h-16 bg-gradient-to-b from-gray-300 to-gray-200 mt-2"></div>
                          )}
                        </div>

                        <div className="flex-1 p-5 ml-6 border border-gray-200 shadow-sm rounded-xl bg-gray-50">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h4 className="text-lg font-bold text-gray-900">
                                <TranslatedText>{event.status}</TranslatedText>
                              </h4>
                              <span className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                                <Calendar className="w-4 h-4" />

                                {event.date ? formatDateTime(event.date) : '-'}
                              </span>
                            </div>
                            {/* <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold ${event.status === complaint.status
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-green-100 text-green-800'
                                }`}
                            >
                              {event.status === complaint.status ? 'Current' : 'Completed'}
                            </span> */}
                          </div>

                          {/* ✅ Scrollable + breakable description container */}
                          <div className="w-full space-y-4 overflow-y-auto max-h-64">
                            <TranslatedText
                              tag="p"
                              className="leading-relaxed text-gray-700 break-all"
                            >
                              {event.description}
                            </TranslatedText>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Public Comments Section */}
              <div className="p-6 mb-8 border shadow-lg rounded-2xl border-blue-200/30 bg-white/80 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-r from-green-500 to-green-600">
                    <MessageCircle className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {t('track.Officer Comments & Updates')}
                  </h3>
                </div>

                {/* Public Comments - Support both API data and mock data */}
                {(() => {
                  // Check if complaint has comments array from API

                  // Add mock comments for demo complaints

                  return (
                    <div
                      className={`space-y-5 ${comments.length > 5 ? 'max-h-80 overflow-y-auto pr-2' : ''}`}
                    >
                      {comments.length > 0 ? (
                        comments.map((comment, index) => (
                          <div
                            key={index}
                            className="p-5 border border-gray-200 shadow-sm rounded-xl bg-gray-50"
                          >
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-center gap-3">
                                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-green-600">
                                  <User className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                  <span className="block font-bold text-gray-900">
                                    <TranslatedText>{comment.author ||
                                      comment.authorName ||
                                      'Officer'}</TranslatedText>
                                  </span>
                                  <span className="px-2 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded-full">
                                    {t('track.Public Comment')}
                                  </span>
                                </div>
                              </div>
                              <div className="text-right">
                                <span className="flex items-center gap-1 text-sm text-gray-600">
                                  <Calendar className="w-4 h-4" />
                                  {comment.commentedAt
                                    ? formatDateTime(comment.commentedAt)
                                    : comment.date
                                      ? formatDateTime(comment.date)
                                      : comment.createdAt
                                        ? formatDateTime(comment.createdAt)
                                        : comment.timestamp
                                          ? formatDateTime(comment.timestamp)
                                          : '-'}
                                </span>
                              </div>
                            </div>
                            <div className="p-4 overflow-y-auto bg-white border border-gray-200 rounded-lg max-h-52">
                              <TranslatedText
                                tag="p"
                                className="text-gray-800 break-all"
                              >
                                {comment.content ||
                                  comment.message ||
                                  comment.text ||
                                  comment.comment}
                              </TranslatedText>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="py-12 text-center border border-gray-200 rounded-xl bg-gray-50">
                          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-full">
                            <MessageCircle className="w-8 h-8 text-gray-400" />
                          </div>
                          <p className="text-lg font-semibold text-gray-600">
                            {t('track.No public comments from officers yet')}
                          </p>
                          <p className="mt-2 text-sm text-gray-500">
                            {t('track.note3')}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Help Text */}
                <div className="p-4 mt-6 border border-blue-200 rounded-xl bg-blue-50">
                  <p className="flex items-center gap-2 text-sm text-blue-800">
                    <MessageCircle className="w-4 h-4" />
                    <strong>{t('track.note4')}</strong> {t('track.note4info')}
                  </p>
                </div>
              </div>
              {/* Reopen Complaint Section */}
              {complaint.status === 'Resolved' && userRole === 'Citizen' && (
                <div className="p-6 mb-8 border shadow-lg rounded-2xl border-yellow-200/30 bg-white/80 backdrop-blur-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-r from-yellow-500 to-yellow-600">
                      <RotateCcw className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {t('track.Reopen Complaint')}
                    </h3>
                  </div>

                  <p className="mb-4 text-gray-700">
                    {t('track.reopenInstruction')}
                  </p>

                  <textarea
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    rows={4}
                    placeholder={t('track.enterReopenReason')}
                    value={reopenReason}
                    onChange={(e) => setReopenReason(e.target.value)}
                  />

                  <div className="flex justify-end mt-4">
                    <button
                      onClick={handleReopenComplaint}
                      disabled={!reopenReason.trim() || reopenLoading}
                      className={`px-6 py-2 rounded-lg text-white font-medium transition-all duration-300 ${reopenReason.trim() && !reopenLoading
                        ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700'
                        : 'bg-gray-300 cursor-not-allowed'
                        }`}
                    >
                      {reopenLoading ? 'Submitting...' : t('track.submitReopen')}
                    </button>
                  </div>
                  {reopenError && (
                    <div className="mt-3 text-sm text-red-600">{reopenError}</div>
                  )}
                  {reopenSuccess && (
                    <div className="mt-3 text-sm text-green-600">{reopenSuccess}</div>
                  )}
                </div>
              )}


              {/* Action Buttons */}
              <div className="flex justify-center pt-6 mt-8 border-t">
                <button
                  onClick={() => setComplaint(null)}
                  className="text-white px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 font-medium flex items-center gap-2"
                  style={{
                    background:
                      'linear-gradient(135deg, #6B7280 0%, #4B5563 50%, #6B7280 100%)',
                  }}
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {showImageModal && selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75"
          onClick={handleCloseImageModal}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedImage.filename}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDownloadFromModal}
                  className="flex items-center gap-2 px-4 py-2 text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  <Download className="w-4 h-4" />
                  {t('dashboard.modal.download')}
                </button>
                <button
                  onClick={handleCloseImageModal}
                  className="p-2 text-gray-500 transition-colors rounded-lg hover:bg-gray-100 hover:text-gray-700"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body - Image Preview */}
            <div className="flex items-center justify-center flex-1 p-4 overflow-auto bg-gray-100">
              <img
                src={selectedImage.url}
                alt={selectedImage.filename}
                className="max-w-full max-h-[calc(90vh-120px)] object-contain rounded-lg"
                onError={(e) => {
                  e.target.src =
                    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EFailed to load image%3C/text%3E%3C/svg%3E';
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TrackComplaintPage;